import * as THREE from 'three'
import { forEachGestureAction } from './GestureChannel.js'

/**
 * Layered Bone Blend — blends a LAYER pose into the BASE pose on one or more
 * bone branches (each bone and everything below it), like Unreal's Layered
 * Blend Per Bone.
 *
 * Modes:
 *   override — inside the branches the layer replaces the base, by `weight`
 *              (rotation slerp, position/scale lerp).
 *   additive — inside the branches the layer's MOTION is added on top of the
 *              base: delta = reference⁻¹ × layer, result = base × delta^weight
 *              (positions: base + (layer − reference) × weight; scale stays
 *              the base's). Unreal "local space" additive.
 *
 * The additive reference ("what counts as zero"):
 *   firstFrame — each layer clip's own first frame (Unreal "Anim Frame 0"):
 *                a gesture that starts neutral adds just its movement.
 *   rest       — the rig's rest pose (Unreal "Skeleton Reference Pose").
 * Clips already baked additive (manifest additive) are always relative to the
 * rest pose, whichever reference is picked — they are deltas already.
 *
 * Outside the branches every bone keeps the base pose — rotation, position and
 * scale.
 *
 * Works at the top of the body graph or inside a state machine state; inside a
 * state it fades in and out with the state's crossfade.
 *
 * Per frame, after the real mixer.update(): evaluate the base alone, the layer
 * alone, then combine per bone. Mixer weights are saved and restored exactly.
 * No per-frame allocation; per-bone storage is built once per scene.
 *
 * The layer's clips are the graph's own players (one per Play Clip node, see
 * GraphEvaluator's play requests): applyWeights keeps them PLAYING at weight 0,
 * so their time advances with the mixer, a new state entry gets new players
 * that start at frame 0, and each node keeps its own Loop and speed. This
 * runtime only lends them weight for the layer pass. The base is likewise the
 * graph's players, not the clip templates, so the passes silence and restore
 * those too (`players` in postUpdate).
 */

// ── Pure blend math (exported for tests). Quaternions/vectors are THREE types.

const _qId    = new THREE.Quaternion()
const _qDelta = new THREE.Quaternion()
const _qPart  = new THREE.Quaternion()
const _qInv   = new THREE.Quaternion()

/** Additive rotation: base × slerp(identity, ref⁻¹ × layer, w). Writes `out`. */
export function additiveQuat(baseQ, layerQ, refQ, w, out) {
  _qInv.copy(refQ).invert()
  _qDelta.multiplyQuaternions(_qInv, layerQ)
  // Shortest arc, so a delta near 360° doesn't spin the long way round.
  if (_qDelta.w < 0) _qDelta.set(-_qDelta.x, -_qDelta.y, -_qDelta.z, -_qDelta.w)
  if (w === 1) return out.multiplyQuaternions(baseQ, _qDelta)
  _qPart.slerpQuaternions(_qId, _qDelta, w)
  return out.multiplyQuaternions(baseQ, _qPart)
}

/** Additive position: base + (layer − ref) × w. Writes `out`. */
export function additivePos(baseP, layerP, refP, w, out) {
  return out.set(
    baseP.x + (layerP.x - refP.x) * w,
    baseP.y + (layerP.y - refP.y) * w,
    baseP.z + (layerP.z - refP.z) * w,
  )
}

// ── State ────────────────────────────────────────────────────────────────────

let _config    = null   // see setConfig
let _lastScene = null
let _bones     = []     // per-bone records, built once per scene (see _discover)
const _byName  = new Map()          // boneName → record
let _branchKey = null               // blendBones the branch flags were built for
let _branchFound = false
// We overwrote bones last frame (see restoreMixerPose).
let _wroteLast = false
const _warned  = new Set()

// Reference poses for "first frame": clipName → { q: Quaternion[], p: Vector3[] }
// indexed like _bones. Built the first time a clip is layered, kept per scene.
const _clipRefs = new Map()
let _restCaptured = false

// Saved mixer weights (restored after the passes): action → effective weight.
const _savedWeights = new Map()

// Scratch.
const _accQ = new THREE.Quaternion()
const _accP = new THREE.Vector3()

export const layeredBoneBlendRuntime = {
  /**
   * @param {null|{blendBones:string[], blendWeight:number, layer:object[],
   *   mode:'override'|'additive', additiveRef:'firstFrame'|'rest'}} config
   *   layer: the layer's play requests ({ clipName, weight, player }), one per
   *   Play Clip node; `player.action` is the node's own player once
   *   applyWeights has run. Their weights are the layer's weight in the graph
   *   — they sum to less than 1 while the node's state crossfades, which fades
   *   the blend.
   */
  setConfig(config) {
    _config = config
  },

  // Called AFTER applyWeights(), BEFORE mixer.update(delta).
  preUpdate() {
    restoreMixerPose()
  },

  /**
   * After mixer.update().
   * @param {THREE.Object3D} scene
   * @param {THREE.AnimationMixer} mixer
   * @param {Record<string, THREE.AnimationAction>} actions  clip templates
   * @param {Record<string, THREE.AnimationAction>} [additiveActions]  additive template clones
   * @param {Set<string>} [additiveClipNames]  manifest-additive clips
   * @param {object[]} [players]  every live graph play request ({ player })
   *   — the base, and the layer's own; silenced for the layer pass, restored after
   */
  postUpdate(scene, mixer, actions, additiveActions, additiveClipNames, players) {
    try {
      if (!_config) return
      const { blendBones, blendWeight, layer, mode, additiveRef } = _config
      if (!blendBones?.length || !layer?.length || blendWeight <= 0) return
      // The layer's clips sum to its weight in the graph (1, or less while its
      // state crossfades). The layer pass plays them normalized — a whole pose,
      // not one faded toward the rest pose — and that sum scales the blend.
      let total = 0
      for (let k = 0; k < layer.length; k++) {
        if (layer[k].weight > 0 && layer[k].player) total += layer[k].weight
      }
      if (total <= 0) return

      if (_lastScene !== scene) {
        _lastScene = scene
        _bones = []
        _byName.clear()
        _branchKey = null
        _clipRefs.clear()
        _restCaptured = false
        _wroteLast = false
        _warned.clear()
        _discover(scene)
      }
      if (!_bones.length) return
      if (!_updateBranches(blendBones)) return

      const additive = mode === 'additive'

      // Save every weight we are about to touch, so the restore is exact.
      _savedWeights.clear()
      for (const clipName in actions) _save(actions[clipName])
      if (additiveActions) for (const clipName in additiveActions) _save(additiveActions[clipName])
      if (players) for (let k = 0; k < players.length; k++) _save(players[k].player?.action)
      for (let k = 0; k < layer.length; k++) _save(layer[k].player?.action)
      // GestureChannel's players are in none of the lists above, and left live
      // they leak into the layer-alone pass. They are set to their envelope
      // weight every frame (no scheduled fades), so the plain save is exact.
      forEachGestureAction(_save)

      // ── PASS 1: base alone (layer clips silent) ───────────────────────────
      for (let k = 0; k < layer.length; k++) layer[k].player?.action.setEffectiveWeight(0)
      mixer.update(0)
      for (const r of _bones) {
        r.baseQ.copy(r.bone.quaternion)
        r.baseP.copy(r.bone.position)
        r.baseS.copy(r.bone.scale)
      }

      // Everything silent from here on: only layer clips (or nothing).
      for (const a of _savedWeights.keys()) a.setEffectiveWeight(0)

      if (additive) {
        // Rest pose = what the mixer writes with nothing playing.
        if (!_restCaptured) {
          mixer.update(0)
          for (const r of _bones) { r.restQ.copy(r.bone.quaternion); r.restP.copy(r.bone.position) }
          _restCaptured = true
        }
        // First frame of each layer clip (once per clip per scene).
        if (additiveRef !== 'rest') {
          for (let k = 0; k < layer.length; k++) {
            const clipName = layer[k].clipName
            if (_clipRefs.has(clipName) || additiveClipNames?.has(clipName)) continue
            const a = layer[k].player?.action
            if (!a) continue
            const t = a.time
            a.time = 0
            a.setEffectiveWeight(1)
            mixer.update(0)
            _clipRefs.set(clipName, {
              q: _bones.map(r => r.bone.quaternion.clone()),
              p: _bones.map(r => r.bone.position.clone()),
            })
            a.setEffectiveWeight(0)
            a.time = t
          }
        }
      }

      // ── PASS 2: layer alone ──────────────────────────────────────────────
      for (let k = 0; k < layer.length; k++) {
        const a = layer[k].player?.action
        if (!a || layer[k].weight <= 0) continue
        a.setEffectiveWeight(layer[k].weight / total)
      }
      mixer.update(0)
      for (const r of _bones) {
        r.layerQ.copy(r.bone.quaternion)
        r.layerP.copy(r.bone.position)
        r.layerS.copy(r.bone.scale)
      }

      // ── BLEND per bone ───────────────────────────────────────────────────
      const w = Math.min(1, blendWeight) * Math.min(1, total)
      for (let i = 0; i < _bones.length; i++) {
        const r = _bones[i]
        const b = r.bone
        if (!r.inBranch) {
          b.quaternion.copy(r.baseQ)
          b.position.copy(r.baseP)
          b.scale.copy(r.baseS)
        } else if (additive) {
          _reference(i, r, layer, total, additiveRef, additiveClipNames)
          additiveQuat(r.baseQ, r.layerQ, _accQ, w, b.quaternion)
          additivePos(r.baseP, r.layerP, _accP, w, b.position)
          b.scale.copy(r.baseS)
        } else {
          b.quaternion.slerpQuaternions(r.baseQ, r.layerQ, w)
          b.position.lerpVectors(r.baseP, r.layerP, w)
          b.scale.lerpVectors(r.baseS, r.layerS, w)
        }
      }

      _wroteLast = true

      // ── Restore the mixer exactly as it was ──────────────────────────────
      // (The layer's players come back to the 0 applyWeights left them at.)
      for (const [a, wt] of _savedWeights) a.setEffectiveWeight(wt)
    } catch (err) {
      console.error(`[LBB] ERROR in postUpdate:`, err)
    }
  },

  /** Debug/test: a bone's captured base/layer pose and branch flag (null if unknown). */
  debugBone(name) {
    const r = _byName.get(name)
    return r ? {
      inBranch: r.inBranch, q: r.bone.quaternion.toArray(), p: r.bone.position.toArray(),
      baseQ: r.baseQ.toArray(), layerQ: r.layerQ.toArray(), baseP: r.baseP.toArray(),
    } : null
  },
}

if (typeof window !== 'undefined') {
  window.__LBB_RUNTIME__ = layeredBoneBlendRuntime
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * three.js only writes a bone when the mixer's result CHANGED since its last
 * evaluation. Our last evaluation was the layer pass, and then we overwrote
 * the bones with the blend — so if the next real update happens to produce
 * that same layer result, the bone would keep our blend and the next base pass
 * would read it back (an additive layer would then pile up frame on frame).
 * Putting the bones back to the mixer's own last result before the real
 * update keeps "bone = what the mixer last produced" true.
 */
function restoreMixerPose() {
  if (!_wroteLast) return
  _wroteLast = false
  for (const r of _bones) {
    r.bone.quaternion.copy(r.layerQ)
    r.bone.position.copy(r.layerP)
    r.bone.scale.copy(r.layerS)
  }
}

function _save(a) {
  if (a) _savedWeights.set(a, a.getEffectiveWeight())
}

/**
 * The additive reference for bone `i` → _accQ / _accP. With several layer
 * clips the per-clip references mix the way the mixer mixes the clips:
 * weighted, with the rest pose filling any weight short of 1. Manifest-
 * additive clips (and the "rest" reference) contribute the rest pose.
 */
function _reference(i, r, layer, total, additiveRef, additiveClipNames) {
  if (additiveRef === 'rest') { _accQ.copy(r.restQ); _accP.copy(r.restP); return }
  let acc = 0
  for (let k = 0; k < layer.length; k++) {
    if (!layer[k].player) continue
    const clipName = layer[k].clipName
    const weight = layer[k].weight / total // as played in the layer pass
    if (weight <= 0 || additiveClipNames?.has(clipName)) continue
    const ref = _clipRefs.get(clipName)
    if (!ref) continue
    acc += weight
    if (acc === weight) { _accQ.copy(ref.q[i]); _accP.copy(ref.p[i]) }
    else {
      const t = weight / acc
      _accQ.slerp(ref.q[i], t)
      _accP.lerp(ref.p[i], t)
    }
  }
  if (acc === 0) { _accQ.copy(r.restQ); _accP.copy(r.restP) }
  else if (acc < 1) { _accQ.slerp(r.restQ, 1 - acc); _accP.lerp(r.restP, 1 - acc) }
}

function _discover(scene) {
  scene.traverse(obj => {
    if (!obj.isSkinnedMesh || !obj.skeleton) return
    for (const bone of obj.skeleton.bones) {
      if (!bone.name || _byName.has(bone.name)) continue
      const r = {
        bone,
        inBranch: false,
        baseQ: new THREE.Quaternion(), baseP: new THREE.Vector3(), baseS: new THREE.Vector3(1, 1, 1),
        layerQ: new THREE.Quaternion(), layerP: new THREE.Vector3(), layerS: new THREE.Vector3(1, 1, 1),
        restQ: new THREE.Quaternion(), restP: new THREE.Vector3(),
      }
      _byName.set(bone.name, r)
      _bones.push(r)
    }
  })
}

/** Flag every bone under any of `blendBones`. False when none of them exist. */
function _updateBranches(blendBones) {
  if (blendBones === _branchKey) return _branchFound
  _branchKey = blendBones
  for (const r of _bones) r.inBranch = false
  let found = 0
  for (const name of blendBones) {
    const root = name && _byName.get(name)
    if (!root) {
      if (name && !_warned.has(name)) { _warned.add(name); console.warn(`[LBB] bone "${name}" not found`) }
      continue
    }
    found++
    root.bone.traverse(o => { const r = _byName.get(o.name); if (r) r.inBranch = true })
  }
  _branchFound = found > 0
  return _branchFound
}
